import { type ComponentProps, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { useStyle } from 'react-native-style-utilities';
import { type Asset } from 'contentful';
import { BodyText, ContentfulImage, theme } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const ProductDetailsNutritionist = (props: Props) => {
  const {
    photo,
    title,
    logo,
    logoWidth = 70,
    logoHeight = 15,
    foregroundColor = theme.colors.WHITE,
  } = props;

  // ─── Styles ──────────────────────────────────────────────────────────

  const titleDynamicStyles = useStyle(
    () => ({ color: foregroundColor, borderColor: foregroundColor }),
    [foregroundColor],
  );

  const photoDynamicStyles = useStyle(
    () => ({ borderColor: foregroundColor }),
    [foregroundColor],
  );

  const logoDynamicStyles = useStyle(
    () => ({ width: logoWidth, height: logoHeight }),
    [logoWidth, logoHeight],
  );

  // ─── Derived Data ────────────────────────────────────────────────────

  const photoUrl = photo?.fields?.file?.url;
  const photoAlt = photo?.fields?.title;

  const logoUrl = logo?.fields?.file?.url;
  const logoAlt = logo?.fields?.title;

  const logoOptions = useMemo<ContentfulImageOptions>(
    () => ({ w: logoWidth * 2, fit: 'scale' }),
    [logoWidth],
  );

  // ─── Styles ──────────────────────────────────────────────────────────

  const photoStyles = [styles.photo, photoDynamicStyles];

  const titleContainerDynamicStyles = useStyle(
    () => ({ borderBottomColor: foregroundColor }),
    [foregroundColor],
  );

  const titleContainerStyles = [
    logo ? [styles.titleContainerWithBorder, titleContainerDynamicStyles] : undefined, // prettier-ignore
  ];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.container}>
      {photoUrl ? (
        <ContentfulImage
          baseUrl={photoUrl}
          options={PHOTO_IMAGE_OPTIONS}
          contentFit="cover"
          style={photoStyles}
          alt={photoAlt}
        />
      ) : null}

      <View style={styles.contentContainer}>
        {title ? (
          <View style={titleContainerStyles}>
            <BodyText sizeMatch={['14']} style={titleDynamicStyles}>
              {title}
            </BodyText>
          </View>
        ) : null}

        {logoUrl ? (
          <ContentfulImage
            baseUrl={logoUrl}
            options={logoOptions}
            contentFit="contain"
            style={logoDynamicStyles}
            alt={logoAlt}
          />
        ) : null}
      </View>
    </View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const PHOTO_SIZE = 40;

const PHOTO_IMAGE_OPTIONS: ContentfulImageOptions = {
  w: PHOTO_SIZE,
  h: PHOTO_SIZE,
  fit: 'fill',
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['4'],
  },
  titleContainerWithBorder: {
    paddingBottom: theme.spacing['2'],
    borderBottomWidth: 1,
  },
  photo: {
    width: PHOTO_SIZE,
    height: PHOTO_SIZE,
    borderRadius: PHOTO_SIZE,
    borderWidth: 1,
  },
  contentContainer: {
    gap: theme.spacing['2'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  photo: Asset | undefined;
  title: string | undefined;
  logo: Asset | undefined;
  logoWidth: number | undefined;
  logoHeight: number | undefined;
  foregroundColor: string | undefined;
};

type ContentfulImageOptions = ComponentProps<typeof ContentfulImage>['options'];
